import { useLabelContext } from '../../../../../context/label'
import type { ProjectLabel } from '@bandos/shared'

interface UseNodeLabelsParams {
  nodeId: string
  mindmapId: string | undefined
  labels: ProjectLabel[] | undefined
}

export const useNodeLabels = ({ nodeId, mindmapId, labels }: UseNodeLabelsParams) => {
  const labelContext = useLabelContext()

  const handleSelectLabel = async (labelId: string) => {
    if (!mindmapId) return

    const selectedLabelIds = labels?.map((l) => l.id) ?? []

    if (selectedLabelIds.includes(labelId)) {
      // Remove label
      const success = await labelContext.removeLabelFromNode(mindmapId, nodeId, labelId)
      if (success) {
        // Update node data through callback (will be handled in MindMapFlow)
        const updatedLabels = labels?.filter((l) => l.id !== labelId) ?? []
        labelContext.setNodeLabels(nodeId, updatedLabels)
      }
    } else {
      // Add label
      // Note: We should NOT manually update cache here since addLabelToNode already handles cache updates
      // and reloadNodeLabels will sync from server. Manual update could cause race conditions.
      await labelContext.addLabelToNode(mindmapId, nodeId, labelId)
    }
  }

  const handleCreateLabel = async (labelText: string, color: string) => {
    if (!mindmapId) return

    const newLabel = await labelContext.createLabel(mindmapId, labelText, color)
    if (newLabel) {
      // Automatically add the new label to this node
      await handleSelectLabel(newLabel.id)
    }
  }

  const handleRemoveLabel = async (labelId: string) => {
    if (!mindmapId) return

    const success = await labelContext.removeLabelFromNode(mindmapId, nodeId, labelId)
    if (success) {
      const updatedLabels = labels?.filter((l) => l.id !== labelId) ?? []
      labelContext.setNodeLabels(nodeId, updatedLabels)
    }
  }

  const handleDeleteLabel = async (labelId: string) => {
    if (!mindmapId) return
    await labelContext.deleteLabel(mindmapId, labelId)
  }

  return {
    labelContext,
    handleSelectLabel,
    handleCreateLabel,
    handleRemoveLabel,
    handleDeleteLabel,
  }
}
